/**
 * ScheduleBlueprintCard.tsx
 * The Live Parsed Preview Card — renders a clean visual summary
 * of the AI-parsed schedule before the user confirms it.
 */

import React, { useState } from 'react';
import { Icons, getAppIcon } from '../../../components/ui/Icons';
import { ParsedSchedule } from '../../../types';
import { cronToHumanReadable } from '../../../services/scheduleParser';

interface ScheduleBlueprintCardProps {
  schedule: ParsedSchedule;
  onConfirm: (schedule: ParsedSchedule) => void;
  onCancel: () => void;
  onEdit?: () => void;
  isSubmitting?: boolean;
}

/** Map tool names to human-friendly labels and icon identifiers */
const getToolDisplay = (toolName: string): { label: string; iconId: string; emoji: string } => {
  const low = toolName.toLowerCase();
  if (low.includes('email') || low.includes('gmail')) return { label: 'Gmail', iconId: 'gmail', emoji: '✉️' };
  if (low.includes('calendar') || low.includes('event')) return { label: 'Google Calendar', iconId: 'calendar', emoji: '📅' };
  if (low.includes('sheet') || low.includes('spreadsheet')) return { label: 'Google Sheets', iconId: 'sheets', emoji: '📊' };
  if (low.includes('doc')) return { label: 'Google Docs', iconId: 'docs', emoji: '📄' };
  if (low.includes('drive') || low.includes('file')) return { label: 'Google Drive', iconId: 'drive', emoji: '📁' };
  if (low.includes('notion')) return { label: 'Notion', iconId: 'notion', emoji: '📝' };
  if (low.includes('github') || low.includes('pr') || low.includes('pull')) return { label: 'GitHub', iconId: 'github', emoji: '🐙' };
  if (low.includes('slack')) return { label: 'Slack', iconId: 'slack', emoji: '💬' };
  return { label: toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), iconId: 'default', emoji: '⚡' };
};

const ScheduleBlueprintCard: React.FC<ScheduleBlueprintCardProps> = ({
  schedule,
  onConfirm,
  onCancel,
  onEdit,
  isSubmitting = false,
}) => {
  const [isExpanded] = useState(true);
  const [localSchedule, setLocalSchedule] = useState<ParsedSchedule>(schedule);
  const [isEditingTrigger, setIsEditingTrigger] = useState(false);
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null);

  // Trigger editing fields
  const [tempName, setTempName] = useState('');
  const [tempCron, setTempCron] = useState('');
  const [tempHuman, setTempHuman] = useState('');

  // Action editing fields
  const [tempToolName, setTempToolName] = useState('');
  const [tempDesc, setTempDesc] = useState('');
  const [tempArgsJson, setTempArgsJson] = useState('');

  React.useEffect(() => {
    setLocalSchedule(schedule);
  }, [schedule]);

  const triggerText = localSchedule?.trigger?.human_readable || 
    (localSchedule?.trigger?.cron_expression ? cronToHumanReadable(localSchedule.trigger.cron_expression) : 'Event Triggered');

  return (
    <div
      className="w-full max-w-[720px] mx-auto mt-4 animate-in"
      style={{ animationDuration: '0.5s' }}
      id="schedule-blueprint-card"
    >
      <div
        className="bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden transition-all duration-300"
        style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}
      >
        {/* ─── Header ─── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-850 bg-gray-50/50 dark:bg-[#171717]/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-[#212121] border border-gray-200 dark:border-gray-800 flex items-center justify-center">
              <Icons.CalendarClock className="w-5 h-5 text-black dark:text-white" />
            </div>
            <div>
              <h3 className="font-bold text-black dark:text-white text-base">{localSchedule?.name || 'New Workflow'}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">Workflow Blueprint</span>
            </div>
          </div>
          <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-gray-100 dark:bg-[#171717] text-black dark:text-gray-250 border border-gray-200 dark:border-gray-800">
            Preview
          </span>
        </div>

        {isExpanded && (
          <div className="p-6 space-y-5">
            {/* ─── Trigger Section ─── */}
            {isEditingTrigger ? (
              <div className="flex flex-col gap-3 p-4 rounded-xl bg-gray-55 dark:bg-[#171717] border border-gray-200 dark:border-gray-800 w-full animate-in fade-in duration-200">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-805">
                  <span className="text-xs font-bold text-black dark:text-white uppercase tracking-wider">Edit Trigger Settings</span>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Workflow Name</label>
                  <input
                    type="text"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white font-semibold"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Cron Expression</label>
                    <input
                      type="text"
                      value={tempCron}
                      onChange={(e) => setTempCron(e.target.value)}
                      className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white font-mono focus:outline-none focus:border-black dark:focus:border-white"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Frequency Description</label>
                    <input
                      type="text"
                      value={tempHuman}
                      onChange={(e) => setTempHuman(e.target.value)}
                      className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end mt-2">
                  <button
                    onClick={() => setIsEditingTrigger(false)}
                    className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setLocalSchedule(prev => ({
                        ...prev,
                        name: tempName,
                        trigger: {
                          ...prev.trigger,
                          cron_expression: tempCron,
                          human_readable: tempHuman
                        }
                      }));
                      setIsEditingTrigger(false);
                    }}
                    className="text-xs font-bold text-white dark:text-black bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 px-4 py-1.5 rounded-lg transition-colors shadow-sm"
                  >
                    Save Trigger
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-55 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 w-full">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#212121] flex items-center justify-center flex-shrink-0 mt-0.5 border dark:border-gray-800">
                  <Icons.Clock className="w-4 h-4 text-black dark:text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold text-black dark:text-white uppercase tracking-[0.12em]">Trigger</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{triggerText}</p>
                  {localSchedule?.trigger?.cron_expression && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-550 mt-1 font-mono">
                      cron: {localSchedule.trigger.cron_expression}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setTempName(localSchedule.name || '');
                    setTempCron(localSchedule.trigger?.cron_expression || '');
                    setTempHuman(localSchedule.trigger?.human_readable || '');
                    setIsEditingTrigger(true);
                  }}
                  className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
                >
                  Edit
                </button>
              </div>
            )}

            {/* ─── Actions Section ─── */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 mb-3 px-1">
                <Icons.Zap className="w-3.5 h-3.5 text-black dark:text-white" />
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-[0.12em]">
                  Actions (Sequential Loop)
                </span>
              </div>

              <div className="space-y-3">
                {(localSchedule?.actions || []).map((action, index) => {
                  const display = getToolDisplay(action?.tool_name || '');
                  const ToolIcon = getAppIcon(display.iconId);
                  const isEditingThisAction = editingActionIndex === index;

                  if (isEditingThisAction) {
                    return (
                      <div key={index} className="flex flex-col gap-3 p-4 rounded-xl bg-gray-55 dark:bg-[#171717] border border-gray-200 dark:border-gray-800 w-full animate-in fade-in duration-200">
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-800">
                          <span className="text-xs font-bold text-black dark:text-white uppercase tracking-wider">Edit Step {index + 1} Action</span>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Tool Name</label>
                          <input
                            type="text"
                            value={tempToolName}
                            onChange={(e) => setTempToolName(e.target.value)}
                            className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white font-mono focus:outline-none focus:border-black dark:focus:border-white"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Step Description</label>
                          <input
                            type="text"
                            value={tempDesc}
                            onChange={(e) => setTempDesc(e.target.value)}
                            className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white font-semibold"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 dark:text-gray-555 uppercase tracking-wider block mb-1">Arguments (JSON)</label>
                          <textarea
                            value={tempArgsJson}
                            onChange={(e) => setTempArgsJson(e.target.value)}
                            rows={4}
                            className="w-full bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white font-mono focus:outline-none focus:border-black dark:focus:border-white resize-y"
                          />
                        </div>
                        <div className="flex gap-2 justify-end mt-2">
                          <button
                            onClick={() => setEditingActionIndex(null)}
                            className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              try {
                                const parsedArgs = JSON.parse(tempArgsJson);
                                setLocalSchedule(prev => {
                                  const actionsCopy = [...(prev.actions || [])];
                                  actionsCopy[index] = {
                                    ...actionsCopy[index],
                                    tool_name: tempToolName,
                                    description: tempDesc,
                                    args: parsedArgs
                                  };
                                  return {
                                    ...prev,
                                    actions: actionsCopy
                                  };
                                });
                                setEditingActionIndex(null);
                              } catch (err: any) {
                                alert(`Invalid JSON arguments: ${err.message}`);
                              }
                            }}
                            className="text-xs font-bold text-white dark:text-black bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 px-4 py-1.5 rounded-lg transition-colors shadow-sm"
                          >
                            Save Step
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 rounded-xl bg-gray-55 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 transition-colors group"
                    >
                      {/* Step number */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 flex items-center justify-center shadow-sm">
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{index + 1}</span>
                        </div>
                        {index < (localSchedule?.actions || []).length - 1 && (
                          <div className="w-px h-6 bg-gray-200 dark:bg-gray-800"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex-shrink-0">
                              <ToolIcon className="w-full h-full" />
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {action?.description || `${display.emoji} ${display.label}`}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setTempToolName(action.tool_name || '');
                              setTempDesc(action.description || '');
                              setTempArgsJson(JSON.stringify(action.args || {}, null, 2));
                              setEditingActionIndex(index);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-[11px] font-bold text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 bg-gray-100 dark:bg-[#212121] px-2 py-0.5 rounded transition-all flex-shrink-0"
                          >
                            Edit Step
                          </button>
                        </div>

                        {/* Parameters */}
                        {action?.args && Object.keys(action.args).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(action.args).map(([key, val]) => (
                              <span
                                key={key}
                                className="inline-flex items-center gap-1 text-[11px] text-gray-550 dark:text-gray-400 bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 rounded-md px-2 py-0.5"
                              >
                                <span className="font-semibold text-gray-600 dark:text-gray-300">{key}:</span>
                                <span className="text-gray-550 dark:text-gray-400 max-w-[150px] truncate">
                                  {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ─── Action Buttons ─── */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(localSchedule)}
                disabled={isSubmitting}
                className={`
                  px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200
                  flex items-center gap-2
                  ${isSubmitting
                    ? 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-600 cursor-not-allowed'
                    : 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Icons.Play className="w-4 h-4" />
                    Confirm & Activate
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleBlueprintCard;
